import { IAddClientAllocation } from '~/services/api/clientAllocation';
import { IAllocationClientsByGroup } from '~/services/api/clientAllocation/types';
import { IClientOption, IClientOptionByTeam, IClientOptionTeam } from '~/store/client/types';
import { IOption } from '~/types';

import {
  ClientWithAllocation,
  LocationGroupWithAllocation,
  NormalizedTeamMembersAllocations,
} from '../shared/model/types';

const resolveTeam = (
  teams?: Partial<IClientOptionTeam>[] | null,
  team?: Partial<IClientOptionTeam> | null,
): Partial<IClientOptionTeam> | null => team ?? teams?.[0] ?? null;

const normalizeClientOptionToAllocation = (
  option: IOption,
  existedAllocationId?: number | null,
): ClientWithAllocation => ({
  id: Number(option.value),
  name: option.name ?? option.label,
  photo: option.photo ?? '',
  userId: option.value as number,
  memo: option.memo ?? '',
  team: resolveTeam(option.teams),
  allocationId: existedAllocationId || null,
  allocation: null,
  clientId: Number(option.value),
  teams: option.teams,
});

const normalizeClientOptionByTeamToAllocation = (
  option: IClientOptionByTeam | IClientOption | ClientWithAllocation,
  existedAllocationId?: number | null,
): ClientWithAllocation => ({
  id: option.id,
  name: option.name,
  photo: option.photo ?? '',
  memo: '',
  userId: option.id,
  team: resolveTeam('teams' in option ? option.teams ?? null : null, option.team ?? null),
  allocation: null,
  allocationId: existedAllocationId ?? null,
  clientId: option.id,
  // eslint-disable-next-line no-nested-ternary
  teams: 'teams' in option ? option.teams ?? null : option.team ? [option.team] : null,
});

export const normalizeClientOptionsToAllocations = (
  options: IOption[],
  existedAllocationId: number,
): ClientWithAllocation[] =>
  options.map(option => normalizeClientOptionToAllocation(option, existedAllocationId));

export const normalizeClientOptionsByTeamToAllocations = (
  options: Array<IClientOptionByTeam | IClientOption | ClientWithAllocation>,
  existedAllocationId: number | null,
): ClientWithAllocation[] =>
  options.map(option => normalizeClientOptionByTeamToAllocation(option, existedAllocationId));

export const buildSyncedClientAllocations = (
  selectedClients: ClientWithAllocation[],
  currentAllocations: ClientWithAllocation[] = [],
): ClientWithAllocation[] => {
  const currentById = new Map(currentAllocations.map(client => [client.id, client]));

  return selectedClients.map(selected => currentById.get(selected.id) ?? selected);
};

export const getClientIdsFromAllocations = (allocations: ClientWithAllocation[]): number[] =>
  allocations.map(client => client.id);

export const getAllSelectedClientIds = (
  teamMembersAllocations: NormalizedTeamMembersAllocations[] = [],
): number[] =>
  Array.from(new Set(teamMembersAllocations.flatMap(member => member.selectedClientsIds ?? [])));

export const normalizeLocationGroupOptionToAllocation = (
  locationGroupId: number,
  locationGroupName: string,
): LocationGroupWithAllocation => ({
  locationGroupId,
  locationGroupName,
  allocation: null,
  clientMemos: {},
  clientTeamIds: {},
  removedClientIds: [],
});

export const buildLocationGroupPayloadForAllocation = (
  teamMembersAllocations: NormalizedTeamMembersAllocations[],
  allocationId: number,
  locationGroups: IAllocationClientsByGroup[],
): { locationGroupIds: number[]; clients: { id: number; memo: string }[] } => {
  const matchingGroupAllocations = teamMembersAllocations.flatMap(member =>
    member.locationGroupAllocations.filter(lg => lg.allocation?.id === allocationId),
  );

  const locationGroupIds = matchingGroupAllocations.map(lg => lg.locationGroupId);

  const memosByGroupId = new Map(
    matchingGroupAllocations.map(lg => [lg.locationGroupId, lg.clientMemos ?? {}]),
  );

  const removedByGroupId = new Map(
    matchingGroupAllocations.map(lg => [lg.locationGroupId, lg.removedClientIds ?? []]),
  );

  const clients = locationGroups
    .filter(lg => locationGroupIds.includes(lg.locationGroup.id))
    .flatMap(lg => {
      const memos = memosByGroupId.get(lg.locationGroup.id) ?? {};
      const removed = removedByGroupId.get(lg.locationGroup.id) ?? [];
      return lg.clients
        .filter(c => !removed.includes(c.id))
        .map(c => ({ id: c.id, memo: memos[c.id] ?? '' }));
    });

  return { locationGroupIds, clients };
};

export const groupLocationGroupAllocationsToAdd = (
  teamMembersAllocations: NormalizedTeamMembersAllocations[],
  locationGroups: IAllocationClientsByGroup[],
): IAddClientAllocation[] =>
  teamMembersAllocations.reduce<IAddClientAllocation[]>((acc, member) => {
    const newGroups = member.locationGroupAllocations.filter(lg => lg.allocation === null);
    if (!newGroups.length) return acc;

    const locationGroupIds = newGroups.map(lg => lg.locationGroupId);
    const memosByGroupId = new Map(newGroups.map(lg => [lg.locationGroupId, lg.clientMemos ?? {}]));
    const teamIdsByGroupId = new Map(
      newGroups.map(lg => [lg.locationGroupId, lg.clientTeamIds ?? {}]),
    );

    const clients = locationGroups
      .filter(lg => locationGroupIds.includes(lg.locationGroup.id))
      .flatMap(lg => {
        const memos = memosByGroupId.get(lg.locationGroup.id) ?? {};
        const teamIds = teamIdsByGroupId.get(lg.locationGroup.id) ?? {};
        const removed =
          newGroups.find(ng => ng.locationGroupId === lg.locationGroup.id)?.removedClientIds ?? [];
        return lg.clients
          .filter(c => !removed.includes(c.id))
          .map(c => ({
            id: c.id,
            memo: memos[c.id] ?? '',
            ...(teamIds[c.id] ? { teamId: teamIds[c.id] } : {}),
          }));
      });

    acc.push({ userId: member.id, clients, locationGroupIds });
    return acc;
  }, []);

export const groupAllocationsToAdd = (
  teamMembersAllocations: NormalizedTeamMembersAllocations[],
): IAddClientAllocation[] =>
  teamMembersAllocations.reduce<IAddClientAllocation[]>((acc, member) => {
    const clients = member.clientsAllocations
      .filter(client => !client.allocation && !client.allocationId)
      .map(client => ({
        id: client.clientId ?? client.id,
        memo: client.memo ?? '',
        ...(client.team?.id ? { teamId: client.team.id } : {}),
      }));

    if (clients.length) {
      acc.push({
        userId: member.id,
        clients,
        locationGroupIds: [],
      });
    }

    return acc;
  }, []);
